import React, { useEffect, useRef, useState } from "react";
import { Checkout, withWorldpay, WithWorldpayProps, Worldpay } from "Custom/Utils/Worldpay/withWorldpay";
import { useObserver } from "mobx-react-lite";
import { forwardRef, useImperativeHandle } from "react";
import { Grid } from "@material-ui/core";
import { WorldpayCardPaymentStyles } from "./CardPayment.styling";

type Props = WithWorldpayProps & {
    checkoutId: string;
    formRef: React.RefObject<HTMLFormElement>;
    onFormValidChange?: (valid: boolean) => void;
};

export type WorldpayCardPaymentRef = {
    generateSessionState: () => Promise<string | null>;
};

const WorldpayCardPaymentInner = forwardRef<WorldpayCardPaymentRef, Props>((props, ref) => {
    const { worldpay, formRef, checkoutId } = props;

    const worldpayCardPaymentStyles = WorldpayCardPaymentStyles();

    const checkout = useRef<Checkout | null>(null);
    const [isReady, setIsReady] = useState(false);

    useImperativeHandle(ref, () => ({
        generateSessionState: () =>
            new Promise(resolve => {
                if (!checkout.current) return resolve(null);

                checkout.current.generateSessionState((error, sessionState) => {
                    if (error) {
                        console.error(error);
                        resolve(null);
                        return;
                    }

                    resolve(sessionState || null);
                });
            }),
    }));

    useEffect(() => {
        if (!formRef.current) return;

        if (isReady) {
            props.onFormValidChange?.(false);
            setIsReady(false);
        }

        let cancelled = false;
        let ready = false;

        const handleFieldChange = (event: Event) => {
            if (cancelled) return;
            const detail = (event as CustomEvent<{ field: { $element: HTMLElement }; "is-valid": boolean }>).detail;
            if (detail["is-valid"]) {
                detail.field.$element.classList.remove("is-invalid-evt");
            } else {
                detail.field.$element.classList.add("is-invalid-evt");
            }
        };

        const handleReady = (event: Event) => {
            if (cancelled) return;
            const detail = (event as CustomEvent<{ "is-ready": boolean }>).detail;
            if (detail["is-ready"]) {
                ready = true;
                if (checkout.current) {
                    setIsReady(true);
                }
            }
        };

        const handleFormChange = (event: Event) => {
            if (cancelled) return;
            const detail = (event as CustomEvent<{ "is-valid": boolean }>).detail;
            props.onFormValidChange?.(detail["is-valid"]);
        };

        formRef.current.addEventListener("wp:form:ready", handleReady);

        worldpay.checkout.init(
            {
                id: checkoutId,
                form: "#card-payment-checkout",
                fields: {
                    pan: {
                        selector: "#worldpay-card-pan",
                        placeholder: "1234 1234 1234 1234",
                    },
                    expiry: {
                        selector: "#worldpay-card-expiry",
                        placeholder: "MM/YY",
                    },
                    cvv: {
                        selector: "#worldpay-card-cvv",
                        placeholder: "CVC",
                    },
                },
                styles: {
                    input: {
                        color: "#000",
                        "font-size": "12px",
                    },
                },
                accessibility: {
                    lang: {
                        locale: "en-GB",
                    },
                    title: {
                        enabled: false,
                    },
                },
                acceptedCardBrands: ["visa", "mastercard"],
                enablePanFormatting: true,
            },
            (error, chkout) => {
                if (error) {
                    console.error(error);
                } else if (chkout && !cancelled) {
                    checkout.current = chkout;
                    if (ready) {
                        setIsReady(true);
                    }

                    if (formRef.current) {
                        formRef.current.addEventListener("wp:form:change", handleFormChange);
                        formRef.current.addEventListener("wp:field:change", handleFieldChange);
                    }
                }
            },
        );

        return () => {
            cancelled = true;

            if (formRef.current) {
                formRef.current.removeEventListener("wp:form:change", handleFormChange);
                formRef.current.removeEventListener("wp:field:change", handleFieldChange);
                formRef.current.removeEventListener("wp:form:ready", handleReady);
            }

            if (checkout.current) {
                checkout.current.remove();
                checkout.current = null;
            }

            props.onFormValidChange?.(false);
            setIsReady(false);
        };
    }, [worldpay, checkoutId]);

    return useObserver(() => (
        <>
            {isReady || (
                <Grid item xs={12}>
                    <p style={{ textAlign: "center" }}>Loading...</p>
                </Grid>
            )}
            <div style={{ display: isReady ? "block" : "none" }}>
                <section>
                    <Grid container spacing={2}>
                        {/* Imitate MUI inputs with classes, to inherit styling (might not be the cleanest but its the best way I can think of) */}
                        <Grid item md={8} sm={8} xs={12}>
                            <section
                                id="worldpay-card-pan"
                                className={`MuiInputBase-input MuiOutlinedInput-input MuiOutlinedInput-root MuiInputBase-fullWidth MuiInputBase-root worldpay-input ${worldpayCardPaymentStyles.worldpayInput}`}
                            />
                        </Grid>
                        <Grid item md={2} sm={2} xs={6}>
                            <section
                                id="worldpay-card-expiry"
                                className={`MuiInputBase-input MuiOutlinedInput-input MuiOutlinedInput-root MuiInputBase-fullWidth MuiInputBase-root worldpay-input ${worldpayCardPaymentStyles.worldpayInput}`}
                            />
                        </Grid>
                        <Grid item md={2} sm={2} xs={6}>
                            <section
                                id="worldpay-card-cvv"
                                className={`MuiInputBase-input MuiOutlinedInput-input MuiOutlinedInput-root MuiInputBase-fullWidth MuiInputBase-root worldpay-input ${worldpayCardPaymentStyles.worldpayInput}`}
                            />
                        </Grid>
                    </Grid>
                </section>
            </div>
        </>
    ));
});

export const WorldpayCardPayment = withWorldpay(WorldpayCardPaymentInner);
